class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    const btn = this.querySelector("button");

    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    let intersected = [];

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      addToCartModule.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      if (intersected.length) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.className === "wt-product__img") {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll("img"),
            );
            const index = thumbnails.indexOf(event.target);

            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}


if (document.querySelector('recharge-subscription-widget')) {
  /* Define CONSTANTS */
  const quantitiesByOption = {
    "0": [1, 1, 1],
    "1": [3, 3, 3],
    "2": [4, 4, 6],
  };
  const reductions = {
    serum: {
      1: 0,
      3: 39,
      4: 34
    },
    shampoing: {
      1: 0,
      3: 17,
      4: 34
    },
    complements: {
      1: 0,
      3: 13,
      6: 36
    },
    cure: {
      1: 0,
      3: 69,
      6: 104
    }
  }
  var quantityArray = [1, 1, 1];
  var productType = '';
  if (document.querySelector('.wt-product__wrapper')) {
    productType = document.querySelector('.wt-product__wrapper').getAttribute('data-product-handle').split('-')[0];
  } else {
    productType = document.querySelector('variant-options').getAttribute('data-product-handle').split('-').includes('cure') ? 'cure' : document.querySelector('variant-options').getAttribute('data-product-handle').split('-')[0];
  }
  
  const economyText = "Économisez 15%"; // Get from settings ? 
  var currentVariant = document.querySelector('product-form input[name="id"]').value;
  
  /* Afficher/Masquer sélecteur de durée. Sélectionner par défaut 1 mois pour souscription */
  function manageSubscription(event) {
    if (event.target.value === "subscription") {
      var discount = rechargesSelector.querySelector('rc-selling-plans')?.shadowRoot;
      discount.querySelector('.rc-plans-button-list .rc-plans-button__discount').textContent = economyText;
      document.querySelector('.global-option-controller').classList.add('s-hidden');
      const radios = document.querySelectorAll('.global-option-controller input[type="radio"]');
      if (radios[0]) {
        radios[0].checked = true;
        radios[0].dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (productType.includes('cure')) {
        const radioBtnActivated = document.querySelector('.wt-product__option input[type="radio"]');
        radioBtnActivated.checked = true;
        radioBtnActivated.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      document.querySelector('.global-option-controller').classList.remove('s-hidden');
    }
  }
  
  /* Mettre à jour les prix dans le widget Recharge */
  function onQuantityChanged(event, index) {
      const quantity = (productType.includes('complements') || productType.includes('cure')) ? quantityArray[2] : quantityArray[0];
      const shadowContainer = document.querySelector('recharge-subscription-widget').shadowRoot;
      
      const initialPrice = parseFloat(document.querySelector('meta[property="og:price:amount"]').content.trim());
      var totalBasePrice = initialPrice * quantity;
      if (productType.includes('cure')) {
        const variants = JSON.parse(document.querySelector('variant-options script[type="application/json"]').textContent);
        totalBasePrice = variants[index]?.price;
        totalBasePrice = totalBasePrice / 100;
      }
      let reduction = reductions[productType]?.[quantity] || 0;
      let finalPrice = totalBasePrice - reduction;
  
      const divContainer = document.createElement('div');
      const originalPriceSpan = document.createElement('span');
      const discountedPriceSpan = document.createElement('span');
      originalPriceSpan.classList.add('rc-price', 'strike-through', 'text-muted');
      if (quantity === 1) {
        originalPriceSpan.style.display = 'none';
      }
      discountedPriceSpan.classList.add('rc-price');
      originalPriceSpan.textContent = totalBasePrice.toFixed(2).replace('.', ',') + '€';
      discountedPriceSpan.textContent = finalPrice.toFixed(2).replace('.', ',') + '€';
      divContainer.appendChild(originalPriceSpan);
  
      const targetDiv = shadowContainer.querySelector('.rc-selection__onetime .rc-purchase-option__prices');
      if (targetDiv) {
          targetDiv.innerHTML = divContainer.innerHTML;
          targetDiv.appendChild(discountedPriceSpan);
      }

      /* Mettre à jour la valeur des inputs qui permettent de transmettre l'information au panier et backoffice */ 
      quantityArray.forEach((quantity, index) => {
        const input = document.getElementById(`composition-${index + 1}`);
        if (input) {
          const productName = input.dataset.original; // récupère la valeur initiale
          input.value = `${quantity} x ${productName}`;
        }
      });
  }

  /* Construire les éléments de réassurance pour le widget Recharge, la réduction se fait via un discount et non directement depuis l'app Recharge */
  function setMarketingOptions(rechargesSelector) {
      var subscription = rechargesSelector.querySelector('.rc-purchase-option__subscription');
      if (subscription) {
        // Badge
        if (!subscription.querySelector('.rc-purchase-option__badge')) {
          var spanBadge = document.createElement('span');
          spanBadge.classList.add('rc-purchase-option__badge');
          spanBadge.setAttribute('aria-hidden', 'true');
          spanBadge.setAttribute('part', 'rc-purchase-option__badge')
          spanBadge.textContent = economyText;
          subscription.insertBefore(spanBadge, subscription.firstChild);  
          // Price reduction
          var spanPrice = document.createElement('span');
          switch(productType) {
            case "serum": 
              spanPrice.textContent = '39,00€';    
              break;
            case "shampoing": 
              spanPrice.textContent = '20,00€';    
              break;
            case "complements": 
              spanPrice.textContent = '16,00€';    
              break;
            default: 
              spanPrice.textContent = '75,00€';    
              break;
          }
          
          rechargesSelector.querySelector('.rc-purchase-option__prices').appendChild(spanPrice);
        } else {
          subscription.querySelector('.rc-purchase-option__badge').textContent = economyText;
          var discount = rechargesSelector.querySelector('rc-selling-plans')?.shadowRoot;
          const observer = new MutationObserver((mutations, obs) => {
            for (var mutation of mutations) {
              if (mutation.type == "childList" && mutation.addedNodes[1]?.classList?.contains('rc-plans-button-list')) {
                discount.querySelector('.rc-plans-button-list .rc-plans-button__discount').textContent = economyText;
                observer.disconnect(); // Stopper l'observation
              }
            }
        });
        observer.observe(discount, { attributes: true, childList: true, subtree: true });
        }
      }
     
  }

  var initialPrice = 0;
  var initialPromotionPrice = 0;
  /* Préselectionner par défaut 1 mois pour la souscription au chargement du widget/page */

	var rechargesSelector = document.querySelector('recharge-subscription-widget').shadowRoot;
	
	const observer = new MutationObserver((mutations, obs) => {
      
      for (var mutation of mutations) {
        var selectorsList = rechargesSelector.querySelectorAll('.rc-purchase-option__input');
        if (mutation.type == "childList" && selectorsList.length) {
          selectorsList.forEach((selector) => {
            selector.addEventListener('change', manageSubscription.bind(this), { bubbles: true });
            
            const radios = document.querySelectorAll('.global-option-controller input[type="radio"]');
            if (radios[0]) {
              radios[0].checked = true;
              radios[0].dispatchEvent(new Event('change', { bubbles: true }));
            }
            if (productType.includes('cure')) {
              const radioBtnActivated = document.querySelector('.wt-product__option input[type="radio"]');
              radioBtnActivated.checked = true;
              radioBtnActivated.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
          setTimeout(() => {
            var spans = rechargesSelector.querySelectorAll('.rc-purchase-option__subscription .rc-purchase-option__prices .rc-price');
            spans.forEach((span, index) => {
                const innerSpan = document.createElement('span');
                if(index > 0) {
                  innerSpan.textContent = span.textContent.split(':')[1];
                  span.style.fontWeight = 600;
                } else {
                  innerSpan.textContent = span.textContent;
                  innerSpan.classList.add('strike-through', 'text-muted');
                }
                span.insertAdjacentElement('afterend', innerSpan);
                span.style.display = "none";
              
            });
          }, 500);
          setMarketingOptions(rechargesSelector);
          observer.disconnect(); // Stopper l'observation
          break;
        }
      }
	});

	observer.observe(rechargesSelector, { attributes: true, childList: true, subtree: true });	
    
    /* Gerer les evenements sur les radio de sélection de la durée de la cure */
    document.querySelectorAll('.global-option-controller input').forEach((input, index) => {

      input.addEventListener('change', (e) => {
        quantityArray = quantitiesByOption[index] || [1, 1, 1];
        if (productType.includes('cure')) {
          var variantSwitcher = document.querySelectorAll('.wt-product__option input[type="radio"]');
          if(variantSwitcher.length) {
            variantSwitcher[index].checked = true;
            variantSwitcher[index].dispatchEvent(new Event('change', { bubbles: true }));
          }
          currentVariant = document.querySelector('variant-options').getAttribute('data-variant-id');
        } 
        onQuantityChanged(e, index);
      });

    });


  document.querySelector('.js-add-to-cart').addEventListener('click', function(e) {
      if (document.querySelector('recharge-subscription-widget').shadowRoot.querySelector('.rc-purchase-option__subscription input[value="subscription"]').checked) {
        return;
      }
      e.preventDefault();
  
      // Récupère le product ID
      const productId = document.querySelector('product-form input[name="id"]').value;
      var quantity = quantityArray[0];
      if (productType.includes('complements')) {
        quantity = quantityArray[2];
      } else if (productType.includes('cure')) {
        quantity = quantitiesByOption[0][0];
      }
    
      this.cart = document.querySelector("cart-drawer");
      this.cart?.setActiveElement(document.activeElement);
  
      // Configuration de la requête avec FormData
      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];
      
      const formData = new FormData();

      formData.append('items[0][id]', productId);
      formData.append('items[0][quantity]', quantity); // Quantité fixe pour le moment
      document.querySelectorAll('input[name^="properties["]').forEach(input => {
        const key = input.name.match(/properties\[(.*)\]/)[1];
        formData.append(`items[0][properties][${key}]`, input.value);
      });
      
      // Ajout des infos pour rafraîchir les sections dynamiques du panier
      formData.append(
        "sections",
        this.cart.getSectionsToRender().map((section) => section.id),
      );
      formData.append("sections_url", window.location.pathname);
      config.body = formData;
      fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((data) => {
        console.log("Ajout au panier réussi", data);
        this.cart.renderContents(data, true);
      })
      .catch((error) => {
        console.error("Erreur lors de l'ajout au panier", error);
      });
  });
}
